import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Users, PawPrint, CalendarCheck, IndianRupee, Clock, TrendingUp, UserCog, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { CURRENCY_SYMBOL } from '../../config/brand';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/dashboard').then(r => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>;
  if (!stats) return <p className="text-[#5A5A5A]">Failed to load dashboard</p>;

  const cards = [
    { label: 'Total Clients', value: stats.total_clients, icon: Users, color: '#4A90E2' },
    { label: 'Total Pets', value: stats.total_pets, icon: PawPrint, color: '#4A7C59' },
    { label: 'Total Bookings', value: stats.total_bookings, icon: CalendarCheck, color: '#E07A5F' },
    { label: "Today's Bookings", value: stats.today_bookings, icon: Clock, color: '#E8B931' },
    { label: 'Pending', value: stats.pending_bookings, icon: AlertCircle, color: '#D9534F' },
    { label: 'Completed', value: stats.completed_bookings, icon: TrendingUp, color: '#4A7C59' },
    { label: 'Staff', value: stats.total_staff, icon: UserCog, color: '#7C3AED' },
    { label: 'Monthly Revenue', value: `${CURRENCY_SYMBOL}${stats.monthly_revenue?.toFixed(0) || 0}`, icon: IndianRupee, color: '#2D2D2D' },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>Dashboard</h1>
        <p className="text-sm text-[#5A5A5A] mt-1">Overview of your grooming business</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <Card key={i} className="rounded-2xl border-[#E5E5E0] bg-white card-hover" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${c.color}15` }}>
                  <c.icon className="w-5 h-5" style={{ color: c.color }} strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-bold text-[#2D2D2D]">{c.value}</p>
              <p className="text-xs text-[#888] mt-1">{c.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Top Services */}
      {stats.top_services?.length > 0 && (
        <Card className="rounded-2xl border-[#E5E5E0] bg-white" style={{ boxShadow: 'var(--shadow-soft)' }}>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-[#2D2D2D] mb-4" style={{ fontFamily: 'Manrope' }}>Top Services</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.top_services}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#5A5A5A' }} />
                <YAxis tick={{ fontSize: 12, fill: '#5A5A5A' }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E5E5E0' }} />
                <Bar dataKey="bookings" fill="#2D2D2D" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Recent Bookings */}
      <Card className="rounded-2xl border-[#E5E5E0] bg-white" style={{ boxShadow: 'var(--shadow-soft)' }}>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-[#2D2D2D] mb-4" style={{ fontFamily: 'Manrope' }}>Recent Bookings</h3>
          <div className="space-y-3">
            {stats.recent_bookings?.slice(0, 5).map(b => (
              <div key={b.appointment_id} className="flex items-center justify-between bg-[#F9F9F7] rounded-xl p-3" data-testid={`recent-booking-${b.appointment_id}`}>
                <div>
                  <p className="text-sm font-medium text-[#2D2D2D]">{b.date} at {b.time_slot}</p>
                  <p className="text-xs text-[#888]">&#8377;{b.total_price}</p>
                </div>
                <span className={`text-xs font-medium px-3 py-1 rounded-full status-${b.status}`}>{b.status}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
